import { DLOB, Zeno } from "mash";
import { OrderMarginType, PublicKey } from "@mashtrade/sdk";
import * as constants from "./constants";
import client from "./client";
import { getZenoApiUrl } from "./config";
import vault from "./vault";
import { VaultDepositor, type Vault } from "@mashtrade/vaults-sdk";

export const fetchHistory = async (
  userAuthority: string | null,
  userSubAccountPublicKey: string | null,
): Promise<Zeno.HistoryRecord[]> => {
  if (!userAuthority) {
    return [];
  }

  const requestUri =
    client.sdk.orderMarginType === OrderMarginType.ISOLATED
      ? `/history/${userAuthority}/` // across all subaccounts!
      : `/history/${userAuthority}/${userSubAccountPublicKey}`;
  const resp = await fetch(getZenoApiUrl(requestUri));
  return (await resp.json()) as Zeno.HistoryRecord[];
};

export const fetchReferralHistory = async (
  userAuthority: string | null,
): Promise<Zeno.ReferralHistoryResult | null> => {
  if (!userAuthority) {
    return null;
  }

  const resp = await fetch(getZenoApiUrl(`/referral-history/${userAuthority}`));
  return (await resp.json()) as Zeno.ReferralHistoryResult;
};

export const fetchBetTickers = async (): Promise<Zeno.BetTicker[]> => {
  const resp = await fetch(getZenoApiUrl("/bets/ticker"));
  const data = (await resp.json()) as Zeno.BetTicker[];

  return data;
};

export const fetchEventByMarketIndicies = async (
  marketIndexes: number[],
): Promise<{ [marketIndex: number]: Zeno.PastMarket }> => {
  const resp = await fetch(
    getZenoApiUrl(`/markets/past?indicies=${marketIndexes.join(",")}`),
  );

  return (await resp.json()) as {
    [marketIndex: number]: Zeno.PastMarket;
  };
};

export const registerEmail = async (
  email: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!constants.Misc.registerEmailUrl) {
      throw new Error(
        "We are sorry, but we are not accepting new users at this time or there is an issue with our email registration service.",
      );
    }

    const response = await fetch(`${constants.Misc.registerEmailUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    // Handle email already registered error
    if (data.error?.message) {
      return {
        success: false,
        message: data.error.message,
      };
    }

    // Handle validation errors
    if (data.detail?.[0]) {
      const errorDetail = data.detail[0];
      return {
        success: false,
        message: errorDetail.ctx?.reason || errorDetail.msg,
      };
    }

    // Handle success case
    if (data.result?.id && data.result?.email) {
      return {
        success: true,
        message: "Email registered successfully",
      };
    }

    return {
      success: false,
      message: "Unknown error occurred",
    };
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Failed to register email",
    };
  }
};

export const fetchPnlHistory = async (address: string) => {
  const resp = await fetch(`${constants.Gordon.dlobHTTP}/user/pnl/${address}`);
  return (await resp.json()) as DLOB.MashUserPnLHistory[];
};

export const fetchMaintenanceStatus = async (): Promise<{
  isMaintanenceMode: boolean;
}> => {
  if (
    typeof window === "undefined" ||
    window.location.host !== "app.mash.fun"
  ) {
    return {
      isMaintanenceMode: false,
    };
  }

  const resp = await fetch(getZenoApiUrl("/status"));
  const data = await resp.json();
  return data as { isMaintanenceMode: boolean };
};

// Content API fetchers
export const fetchBanners = async (): Promise<Zeno.Banners> => {
  const resp = await fetch(getZenoApiUrl("/content/banners"));
  return (await resp.json()) as Zeno.Banners;
};

export const fetchHowItWorks = async (): Promise<Zeno.HowItWorks> => {
  const resp = await fetch(getZenoApiUrl("/content/how-it-works"));
  return (await resp.json()) as Zeno.HowItWorks;
};

export const fetchCTABoxes = async (): Promise<Zeno.CTABoxes> => {
  const resp = await fetch(getZenoApiUrl("/content/cta-boxes"));
  return (await resp.json()) as Zeno.CTABoxes;
};

export const fetchMainCTA = async (): Promise<{
  loggedIn: Zeno.MainCTA;
  loggedOut: Zeno.MainCTA;
}> => {
  const resp = await fetch(getZenoApiUrl("/content/main-cta"));
  const data = (await resp.json()) as Array<Zeno.MainCTA>;

  return {
    loggedIn: data.find((cta) => cta.loggedIn) as Zeno.MainCTA,
    loggedOut: data.find((cta) => !cta.loggedIn) as Zeno.MainCTA,
  };
};

export const fetchComms = async (): Promise<Zeno.Comms> => {
  const resp = await fetch(getZenoApiUrl("/content/comms"));
  return (await resp.json()) as Zeno.Comms;
};

// Vault fetchers
export const fetchVault = async (vaultAddress: string) => {
  if (!vault.isInitialized()) {
    throw new Error("Vault client not initialized");
  }

  return await vault.getVault(new PublicKey(vaultAddress));
};

export const fetchVaultEquity = async (
  vaultAddress: string,
  vaultData?: Vault,
) => {
  if (!vault.isInitialized()) {
    throw new Error("Vault client not initialized");
  }

  return await vault.calculateVaultEquity({
    address: vaultData ? undefined : new PublicKey(vaultAddress),
    vault: vaultData,
    factorUnrealizedPNL: true,
  });
};

export const fetchVaultDepositor = async (depositorAddress: string) => {
  if (!vault.isInitialized()) {
    throw new Error("Vault client not initialized");
  }

  return await vault.getVaultDepositor(new PublicKey(depositorAddress));
};

export const fetchVaultDepositorEquity = async (
  depositorAddress: string,
  vaultAddress: string,
  depositorData?: VaultDepositor,
  vaultData?: Vault,
) => {
  if (!vault.isInitialized()) {
    throw new Error("Vault client not initialized");
  }

  return await vault.calculateWithdrawableVaultDepositorEquityInDepositAsset({
    vaultDepositorAddress: depositorData
      ? undefined
      : new PublicKey(depositorAddress),
    vaultDepositor: depositorData,
    vaultAddress: vaultData ? undefined : new PublicKey(vaultAddress),
    vault: vaultData,
  });
};
